import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Input, Button } from 'shared/components';
import { ValidationService } from 'shared/services';
import { DATE_FORMATS } from 'shared/constants';
import moment from 'moment';
import './TimeLog.scss';

const isTimeAm = (data) => moment(data).hours() < 12;

const validateTime = (value, prevValue) => {
  switch (value.length) {
    case 1:
      return +value > 1 ? `0${value}:` : value;
    case 2:
      if (prevValue.length > value.length) {
        return value.slice(0, 1);
      }

      if (Number.isNaN(+value)) {
        return value.slice(0, value.length - 1);
      }

      if (+value > 12) {
        return +value < 15 ? `0${value[0]}:${value[1]}` : `0${value[0]}:0${value[1]}`;
      }

      return `${value}:`;
    case 3:
      if (value.indexOf(':') !== 2) {
        return prevValue;
      }
      return value;
    case 4:
      if (value.indexOf(':') !== 2) {
        return prevValue;
      }
      return value.substr(value.length - 1) > 5
        ? `${value.slice(0, value.length - 1)}0${value[value.length - 1]}`
        : value;
    default:
      return value;
  }
};

class TimeLog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      duration: props.irregularity.duration ? props.irregularity.duration.toString() : '',
      startDateTime: this.props.irregularity.startDateTime,
      time: props.irregularity.startDateTime
        ? moment(props.irregularity.startDateTime).format('hh:mm')
        : '',
      timeAm: props.irregularity.startDateTime ? isTimeAm(props.irregularity.startDateTime) : true,
      errors: props.errors,
    };

    this.onChangeMask = this.onChangeMask.bind(this);
    this.toggleAmPm = this.toggleAmPm.bind(this);
    this.onDurationChange = this.onDurationChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.irregularity.duration !== this.props.irregularity.duration ||
      prevProps.irregularity.startDateTime !== this.props.irregularity.startDateTime ||
      prevProps.errors !== this.props.errors
    ) {
      this.setState({
        duration: this.props.irregularity.duration
          ? this.props.irregularity.duration.toString()
          : '',
        startDateTime: this.props.irregularity.startDateTime,
        // time: this.props.irregularity.startDateTime ? moment(this.props.irregularity.startDateTime).format('hh:mm') : '',
        errors: this.props.errors,
      });
    }
  }

  onDurationChange(e) {
    if (e.target.value.length > 3) {
      return;
    }
    if (!ValidationService.numeric(+e.target.value)) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          duration: ['errors.onlyNumbersAllowed'],
        },
      }));
      return;
    }

    this.setState({
      errors: {},
    });

    this.props.onChange({ duration: +e.target.value });
  }

  onChangeMask(e) {
    let { value } = e.target;

    if (value.length === 2 && +value === 0) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          time: 'errors.invalidTimeFormat',
        },
      }));
      return;
    }
    if (value.length > 5 || !ValidationService.clock(value)) {
      return;
    }

    value = validateTime(value, this.state.time);

    let time = value.replace(':', '');
    const momentTime = moment(time, 'hmm');

    if (time.length === 4 && momentTime.isValid()) {
      time = momentTime.format('hh:mm');
      this.props.onChange({
        startDateTime: moment(`${time} ${this.state.timeAm ? 'AM' : 'PM'}`, ['h:mm A']).format(
          DATE_FORMATS.dateTimeZone
        ),
      });
    } else {
      this.props.onChange({
        startDateTime: '',
      });
    }

    this.setState({
      time: value,
    });
  }

  toggleAmPm() {
    if (this.state.startDateTime) {
      const time = moment(this.state.time.replace(':', ''), 'hmm');
      this.props.onChange({
        startDateTime: moment(
          `${moment(time).format('hh:mm')} ${!this.state.timeAm ? 'AM' : 'PM'}`,
          ['h:mm A']
        ).format(DATE_FORMATS.dateTimeZone),
      });
    }

    this.setState((prevState) => ({
      timeAm: !prevState.timeAm,
    }));
  }

  render() {
    const { errors } = this.state;

    return (
      <div className="mt-4 time-log-container">
        <p className="text-strong m-0 pb-2">{this.props.t('irregularities.timeLogTitle')}</p>
        <div className="list-group-item">
          <label className={`${errors.time ? 'label-error' : ''} w-100 pt-2`} htmlFor="timePicker">
            {this.props.t('forms.labels.timeOfOccurrence')}
          </label>
          <div>
            <input
              className={`${errors.time ? 'error' : ''} time-input`}
              type="text"
              id="timePicker"
              placeholder="hh:mm"
              value={this.state.time}
              onChange={this.onChangeMask}
            />
            <div className="time-buttons-container d-inline-block ps-2">
              <Button
                type={this.state.timeAm ? 'btn-primary' : 'btn-outline btn-primary'}
                className="w-50 m-0 btn-selector"
                text={this.props.t('common.labels.amTime')}
                onClick={this.toggleAmPm}
              />
              <Button
                type={!this.state.timeAm ? 'btn-primary' : 'btn-outline btn-primary'}
                className="w-50 m-0 btn-selector"
                onClick={this.toggleAmPm}
                text={this.props.t('common.labels.pmTime')}
              />
            </div>
            {errors.time && (
              <span className="text-error font-size-12">{this.props.t(errors.time)}</span>
            )}
          </div>
          <Input
            className="w-100"
            type="number"
            name="duration"
            value={this.state.duration}
            onChange={this.onDurationChange}
            label={this.props.t('forms.labels.duration')}
            errors={errors.duration}
            placeholder="mm"
          />
        </div>
      </div>
    );
  }
}

export default withTranslation()(TimeLog);
TimeLog.propTypes = {
  irregularity: PropTypes.shape({
    duration: PropTypes.number,
    startDateTime: PropTypes.string,
  }).isRequired,
  errors: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
